import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import backendVerifiedUser from "../../../api/backendVerifiedUser";
import backendValidUserForFile from "../../../api/backendVerifiedUserForFile";
import { SET_ERROR, SET_SUCCESS } from "../../../store/Types/AuthTypes";

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";

const emptyForm = { name: "", link: "", is_active: true, file: null, preview: null };

// Brand logos shown in the "brands" strip on the storefront home page (Fancy theme).
// Only active logos appear on the website.
export default function BrandLogos() {
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // logo row being edited, or null for "add"
  const [showForm, setShowForm] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const notify = (type, msg) => msg && dispatch({ type, payLoad: msg });
  const errorMsg = (err) => err?.response?.data?.error?.msg || "Something went wrong";

  const load = async () => {
    setLoading(true);
    try {
      const res = await backendVerifiedUser.get("/brand-logos");
      setLogos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      notify(SET_ERROR, errorMsg(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (logo) => {
    setEditing(logo);
    setForm({ name: logo.name, link: logo.link || "", is_active: !!logo.is_active, file: null, preview: logo.image_path });
    setShowForm(true);
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      notify(SET_ERROR, "Only PNG, JPG, WEBP or GIF images are allowed.");
      return;
    }
    if (file.size > MAX_BYTES) {
      notify(SET_ERROR, "Logo must be 2MB or smaller.");
      return;
    }
    setForm((f) => ({ ...f, file, preview: URL.createObjectURL(file) }));
  };

  const linkInvalid = form.link.trim() !== "" && !/^https?:\/\//i.test(form.link.trim());
  const canSave = form.name.trim() && (editing || form.file) && !linkInvalid && !saving;

  const save = async () => {
    if (!canSave) return;
    const data = new FormData();
    data.append("name", form.name.trim());
    data.append("link", form.link.trim());
    data.append("is_active", form.is_active ? "1" : "0");
    if (form.file) data.append("logo", form.file);
    setSaving(true);
    try {
      const res = editing
        ? await backendValidUserForFile.post(`/brand-logos/${editing.id}`, data)
        : await backendValidUserForFile.post("/brand-logos", data);
      notify(SET_SUCCESS, res.data?.success?.msg);
      setShowForm(false);
      await load();
    } catch (err) {
      notify(SET_ERROR, errorMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (logo) => {
    // optimistic
    setLogos((list) => list.map((l) => (l.id === logo.id ? { ...l, is_active: l.is_active ? 0 : 1 } : l)));
    try {
      await backendVerifiedUser.get(`/brand-logos/${logo.id}/toggle`);
    } catch (err) {
      notify(SET_ERROR, errorMsg(err));
      load();
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= logos.length) return;
    const a = logos[index];
    const b = logos[target];
    const next = [...logos];
    next[index] = b;
    next[target] = a;
    setLogos(next);
    try {
      // Re-number both rows by their new positions so ordering is stable
      await Promise.all([
        backendValidUserForFile.post(`/brand-logos/${a.id}`, toForm({ sort_order: target + 1 })),
        backendValidUserForFile.post(`/brand-logos/${b.id}`, toForm({ sort_order: index + 1 })),
      ]);
    } catch (err) {
      notify(SET_ERROR, errorMsg(err));
      load();
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await backendVerifiedUser.post("/brand-logos/delete", { ids: [toDelete.id] });
      notify(SET_SUCCESS, res.data?.success?.msg);
      setToDelete(null);
      load();
    } catch (err) {
      notify(SET_ERROR, errorMsg(err));
    }
  };

  const activeCount = logos.filter((l) => l.is_active).length;

  return (
    <div className="card shadow-md rounded-lg p-4 mx-4 mt-3">
      <Divider>
        <span className="text-2xl font-bold p-font-style text-center text-black mx-1">Brand Logos</span>
      </Divider>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="m-0 text-sm text-gray-600">
          Logos shown in the brands strip on the home page (Fancy theme). Only <strong>active</strong> logos appear
          on the website — {activeCount} of {logos.length} active.
        </p>
        <Button label="Add Logo" icon="pi pi-plus" className="p-primary-btn" onClick={openAdd} />
      </div>

      {loading ? (
        <div className="text-center py-6 text-gray-500">
          <i className="pi pi-spin pi-spinner mr-2" /> Loading…
        </div>
      ) : logos.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-500">
          <i className="pi pi-images text-3xl mb-2 block" />
          No brand logos yet. Click <strong>Add Logo</strong> to upload one.
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
          {logos.map((logo, i) => (
            <div
              key={logo.id}
              className="border rounded-lg p-3 flex flex-col gap-2"
              style={{ opacity: logo.is_active ? 1 : 0.6, background: "#fff" }}
            >
              <div className="flex items-center justify-center rounded" style={{ height: 90, background: "#f8f9fa" }}>
                <img src={logo.image_path} alt={logo.name} style={{ maxHeight: 70, maxWidth: "85%", objectFit: "contain" }} />
              </div>
              <div className="font-semibold text-sm truncate" title={logo.name}>{logo.name}</div>
              {logo.link ? (
                <a href={logo.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 truncate">{logo.link}</a>
              ) : (
                <span className="text-xs text-gray-400">No link</span>
              )}
              <div className="flex items-center justify-between mt-1">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <InputSwitch checked={!!logo.is_active} onChange={() => toggle(logo)} />
                  {logo.is_active ? "Active" : "Inactive"}
                </label>
                <div className="flex gap-1">
                  <Button icon="pi pi-arrow-left" rounded text size="small" disabled={i === 0} onClick={() => move(i, -1)} tooltip="Move earlier" />
                  <Button icon="pi pi-arrow-right" rounded text size="small" disabled={i === logos.length - 1} onClick={() => move(i, 1)} tooltip="Move later" />
                  <Button icon="pi pi-pencil" rounded text size="small" onClick={() => openEdit(logo)} tooltip="Edit" />
                  <Button icon="pi pi-trash" rounded text severity="danger" size="small" onClick={() => setToDelete(logo)} tooltip="Delete" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit dialog */}
      <Dialog
        header={editing ? "Edit Brand Logo" : "Add Brand Logo"}
        visible={showForm}
        style={{ width: "min(460px, 95vw)" }}
        onHide={() => !saving && setShowForm(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setShowForm(false)} disabled={saving} />
            <Button label={saving ? "Saving…" : "Save"} icon="pi pi-check" className="p-primary-btn" onClick={save} disabled={!canSave} />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Logo image {editing ? "(optional — replace)" : "*"}</label>
            <div
              className="flex items-center justify-center border-2 border-dashed rounded-lg cursor-pointer"
              style={{ height: 120, background: "#f8f9fa" }}
              onClick={() => fileRef.current?.click()}
            >
              {form.preview ? (
                <img src={form.preview} alt="Preview" style={{ maxHeight: 96, maxWidth: "80%", objectFit: "contain" }} />
              ) : (
                <span className="text-sm text-gray-500"><i className="pi pi-upload mr-2" />Click to choose an image</span>
              )}
            </div>
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" style={{ display: "none" }} onChange={pickFile} />
            <small className="text-gray-500">PNG, JPG, WEBP or GIF — max 2MB. A transparent PNG looks best.</small>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Brand name *</label>
            <InputText className="w-full" value={form.name} maxLength={150} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Link (optional)</label>
            <InputText
              className={`w-full ${linkInvalid ? "p-invalid" : ""}`}
              placeholder="https://example.com"
              value={form.link}
              onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
            />
            {linkInvalid && <small className="p-error">Link must start with http:// or https://</small>}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <InputSwitch checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.value }))} />
            Show on website
          </label>
        </div>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        header="Delete brand logo?"
        visible={!!toDelete}
        style={{ width: "min(380px, 95vw)" }}
        onHide={() => setToDelete(null)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setToDelete(null)} />
            <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={confirmDelete} />
          </div>
        }
      >
        <p className="m-0 text-sm">
          <strong>{toDelete?.name}</strong> will be removed from the list and the website.
        </p>
      </Dialog>
    </div>
  );
}

function toForm(obj) {
  const data = new FormData();
  Object.entries(obj).forEach(([k, v]) => data.append(k, String(v)));
  return data;
}
